/**
 * BookingsPage — list of the user's own bookings, with cancel + link to book.
 *
 * Reads via api-bookings GET /bookings?mine=true (server filters by user_id).
 * Booking keys are rendered inline on the booking (keys_v1 rows referenced by
 * issued_key_ids, readable by the owner via RLS): a booking guest usually has
 * no device assignment, so the device-tile list on KeysPage would show nothing
 * for them. The key link sent by SMS/email lands here as /bookings?booking=<id>.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Calendar, Plus, ChevronRight, X, Key, Clock } from 'lucide-react';
import { Header } from '@/components/layout/Header';
import { KeyCard } from '@/components/keys/KeyCard';
import { bookingApi, type BookingRow, type BookingResource } from '@/lib/bookingApi';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import type { AccessKey } from '@/types';

const statusLabels: Record<string, string> = {
  reserved: 'Reserverad',
  paid: 'Bekräftad',
  active: 'Aktiv',
  ended: 'Avslutad',
  canceled: 'Avbokad',
};

const statusColors: Record<string, string> = {
  reserved: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  paid: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  active: 'bg-blue-500/15 text-blue-700 dark:text-blue-300',
  ended: 'bg-zinc-500/15 text-zinc-700 dark:text-zinc-300',
  canceled: 'bg-rose-500/15 text-rose-700 dark:text-rose-300',
};

function formatRange(fromIso: string, toIso: string) {
  const fmt = new Intl.DateTimeFormat('sv-SE', { dateStyle: 'short', timeStyle: 'short' });
  return `${fmt.format(new Date(fromIso))} – ${fmt.format(new Date(toIso))}`;
}

export default function BookingsPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const highlightedBookingId = searchParams.get('booking');
  const [bookings, setBookings] = useState<BookingRow[]>([]);
  const [resources, setResources] = useState<BookingResource[]>([]);
  const [keys, setKeys] = useState<AccessKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const [b, r] = await Promise.all([
        bookingApi.myBookings(),
        bookingApi.listResources().catch(() => ({ resources: [] as BookingResource[] })),
      ]);
      setBookings(b.bookings);
      setResources(r.resources);

      // Active keys for these bookings (owner-scoped by RLS on keys_v1).
      const keyIds = b.bookings.flatMap((row) => row.issued_key_ids ?? []);
      if (keyIds.length > 0) {
        const { data: keyRows } = await supabase
          .from('keys_v1')
          .select('*')
          .in('id', keyIds)
          .eq('status', 'active')
          .gte('expires_at', new Date().toISOString());
        setKeys((keyRows ?? []) as AccessKey[]);
      } else {
        setKeys([]);
      }
    } catch (e: any) {
      setError(e?.message ?? 'Kunde inte ladda bokningar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) load();
  }, [user]);

  // Not signed in: the key link from SMS/email must survive the login round
  // trip, so hand the full path to /auth and come back here afterwards.
  useEffect(() => {
    if (!authLoading && !user) {
      const next = `/bookings${highlightedBookingId ? `?booking=${encodeURIComponent(highlightedBookingId)}` : ''}`;
      navigate(`/auth?next=${encodeURIComponent(next)}`, { replace: true });
    }
  }, [authLoading, user, highlightedBookingId, navigate]);

  const keysById = useMemo(() => new Map(keys.map((k) => [k.id, k])), [keys]);

  // Bring the linked booking to the top so the customer lands on their key.
  const orderedBookings = useMemo(() => {
    if (!highlightedBookingId) return bookings;
    const hit = bookings.find((b) => b.id === highlightedBookingId);
    return hit ? [hit, ...bookings.filter((b) => b.id !== highlightedBookingId)] : bookings;
  }, [bookings, highlightedBookingId]);

  const handleCancel = async (id: string) => {
    if (!confirm('Avboka denna bokning?')) return;
    try {
      await bookingApi.cancelBooking(id);
      load();
    } catch (e: any) {
      alert(`Kunde inte avboka: ${e?.message ?? ''}`);
    }
  };

  return (
    <>
      <Header />
      <main className="px-4 py-6 max-w-2xl mx-auto space-y-6">
        <h2 className="text-2xl font-semibold tracking-tight">Bokningar</h2>

        {resources.length > 0 && (
          <section className="rounded-3xl glass-regular p-4">
            <h3 className="text-sm font-semibold mb-3">Boka resurs</h3>
            <div className="grid gap-2">
              {resources.filter((r) => r.is_active).map((r) => (
                <button
                  key={r.id}
                  onClick={() => navigate(`/book/${r.id}`)}
                  className="flex items-center justify-between rounded-2xl glass-thin p-3.5 transition-all hover:scale-[1.01] active:scale-[0.98]"
                >
                  <span className="font-medium">{r.name}</span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </button>
              ))}
            </div>
          </section>
        )}

        <section>
          <h3 className="text-sm font-semibold mb-3">Mina bokningar ({bookings.length})</h3>

          {loading && <p className="text-muted-foreground">Laddar…</p>}
          {error && <p className="text-rose-500">{error}</p>}
          {!loading && bookings.length === 0 && (
            <div className="rounded-3xl glass-thin p-8 text-center">
              <Calendar className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
              <p className="text-muted-foreground">Du har inga bokningar än.</p>
            </div>
          )}

          <div className="grid gap-3">
            {orderedBookings.map((b) => {
              const bookingKeys = (b.issued_key_ids ?? [])
                .map((id) => keysById.get(id))
                .filter((k): k is AccessKey => Boolean(k));
              const isHighlighted = b.id === highlightedBookingId;
              return (
              <article
                key={b.id}
                className={`rounded-3xl glass-thin p-4 ${isHighlighted ? 'ring-2 ring-primary' : ''}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="h-3.5 w-3.5" />
                      <span>{formatRange(b.from_ts, b.to_ts)}</span>
                    </div>
                    {b.status === 'paid' && bookingKeys.length === 0 && (
                      <p className="mt-2 inline-flex items-center gap-1 text-sm text-muted-foreground">
                        <Key className="h-3.5 w-3.5" />
                        Nyckeln aktiveras när bokningen börjar
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${statusColors[b.status] ?? statusColors.reserved}`}>
                      {statusLabels[b.status] ?? b.status}
                    </span>
                    {(b.status === 'reserved' || b.status === 'paid') && (
                      <button
                        onClick={() => handleCancel(b.id)}
                        className="text-xs text-rose-500 hover:text-rose-600 inline-flex items-center gap-1"
                      >
                        <X className="h-3 w-3" /> Avboka
                      </button>
                    )}
                  </div>
                </div>
                {bookingKeys.length > 0 && (
                  <div className="mt-3 grid gap-3">
                    {bookingKeys.map((k) => (
                      <KeyCard key={k.id} accessKey={k} />
                    ))}
                  </div>
                )}
              </article>
              );
            })}
          </div>
        </section>
      </main>
    </>
  );
}
